import { useState, useCallback } from 'react';
import { NearbyResult, NearbyType } from '@habity/types';
import { useToast } from '@chakra-ui/react';
import { NEARBY } from '@habity/constants';
import { deduplicateNearbyItems } from '~/lib/homes/profile/nearbyHelpers';

export type NearbyEditItem = {
  id: string; // Temporary ID for editing (use place_id if available, otherwise generate)
  type: NearbyType;
  name: string;
  distance: number;
  address?: string;
  // Keep original fields for compatibility
  place_id?: string;
  osm_type?: string;
  lat?: string;
  lon?: string;
  class?: string;
  tag_type?: NearbyType;
  display_name?: string;
  boundingbox?: unknown;
};

export const useNearbyEditor = (
  initialItems: NearbyResult[] | null | undefined,
  onSave: (items: NearbyResult[]) => Promise<void>
) => {
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editItems, setEditItems] = useState<NearbyEditItem[]>([]);

  // Convert NearbyResult to NearbyEditItem
  const convertToEditItem = useCallback((item: NearbyResult): NearbyEditItem => {
    return {
      id: item.place_id || `temp-${Date.now()}-${Math.random()}`,
      type: item.type || item.tag_type || 'supermarket', // Fallback to tag_type or default
      name: item.name || '',
      distance: item.distance || 0,
      address: item.display_name || item.address?.name || '',
      // Preserve original fields
      place_id: item.place_id,
      osm_type: item.osm_type,
      lat: item.lat,
      lon: item.lon,
      class: item.class,
      tag_type: item.tag_type,
      display_name: item.display_name,
      boundingbox: item.boundingbox,
    };
  }, []);

  // Convert NearbyEditItem back to NearbyResult
  const convertToNearbyResult = useCallback((item: NearbyEditItem): NearbyResult => {
    return {
      place_id: item.place_id || item.id,
      osm_type: item.osm_type || 'node',
      lat: item.lat || '0',
      lon: item.lon || '0',
      class: item.class || 'amenity',
      type: item.type,
      tag_type: item.tag_type || item.type,
      name: item.name,
      display_name: item.display_name || item.address || item.name,
      address: item.address ? { name: item.address } : {},
      boundingbox: item.boundingbox || [],
      distance: item.distance,
    };
  }, []);

  const handleEdit = useCallback(() => {
    const items = initialItems || [];
    
    // Get valid nearby types from constants
    const validTypes = new Set(NEARBY.map(n => n.key));
    
    // Debug: Log the original items to check types
    if (items.length > 0) {
      console.log('Original nearby items:', items);
      console.log('First item type:', items[0]?.type, 'tag_type:', items[0]?.tag_type);
    }
    
    // Filter out items with types that don't exist in NEARBY constants
    const filteredItems = items.filter(item => {
      const itemType = item.type || item.tag_type;
      return itemType && validTypes.has(itemType);
    });
    
    // Deduplicate items by place_id and name (keep the closest one for each unique location)
    const deduplicatedItems = deduplicateNearbyItems(filteredItems);
    const editableItems = deduplicatedItems.map(convertToEditItem);
    
    // Debug: Log converted items
    if (editableItems.length > 0) {
      console.log('Converted edit items (deduplicated):', editableItems);
      console.log('First edit item type:', editableItems[0]?.type);
    }
    
    // If no items exist, add one empty item to start with
    if (editableItems.length === 0) {
      editableItems.push({
        id: `temp-${Date.now()}-${Math.random()}`,
        type: 'supermarket',
        name: '',
        distance: 0,
        address: '',
      });
    }
    
    setEditItems(editableItems);
    setIsEditing(true);
  }, [initialItems, convertToEditItem]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditItems([]);
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const nearbyResults = editItems.map(convertToNearbyResult);
      await onSave(nearbyResults);
      
      toast({
        title: 'Närliggande platser uppdaterade',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setIsEditing(false);
      setEditItems([]);
    } catch (error) {
      console.error('Error saving nearby items:', error);
      toast({
        title: 'Kunde inte spara ändringar',
        description: 'Försök igen senare',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [editItems, onSave, convertToNearbyResult, toast]);

  const handleAddItem = useCallback(() => {
    const newItem: NearbyEditItem = {
      id: `temp-${Date.now()}-${Math.random()}`,
      type: 'supermarket',
      name: '',
      distance: 0,
      address: '',
    };
    setEditItems([...editItems, newItem]);
  }, [editItems]);

  const handleRemoveItem = useCallback((id: string) => {
    setEditItems(editItems.filter(item => item.id !== id));
  }, [editItems]);

  const handleUpdateItem = useCallback((id: string, updates: Partial<NearbyEditItem>) => {
    setEditItems(editItems.map(item => 
      item.id === id ? { ...item, ...updates } : item
    ));
  }, [editItems]);

  return {
    isEditing,
    isSaving,
    editItems,
    handleEdit,
    handleCancel,
    handleSave,
    handleAddItem,
    handleRemoveItem,
    handleUpdateItem,
  };
};
